import { useState, type ReactNode } from 'react';
import { Brand } from './brand';
import { Button } from './button';
import { Notice } from './feedback';
import { Field, Input } from './input';

/** The frame every sign-in screen shares: the brand over one card. */
export function AuthScreen({
  brandSubtitle,
  children,
  footer,
}: {
  brandSubtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="flex min-h-dvh items-center justify-center floor px-4 py-10">
      <div className="w-full max-w-[400px]">
        <div className="mb-8 flex justify-center">
          <Brand subtitle={brandSubtitle} />
        </div>
        <div className="space-y-5 rounded-xl border border-line bg-surface p-6 shadow-hairline sm:p-8">
          {children}
        </div>
        {footer && <div className="mt-6 text-center text-sm text-ink-3">{footer}</div>}
      </div>
    </div>
  );
}

export function AuthHeading({ title, subtitle }: { title: string; subtitle?: ReactNode }) {
  return (
    <div>
      <h1 className="text-xl font-semibold">{title}</h1>
      {subtitle && <p className="mt-1 text-base text-ink-3">{subtitle}</p>}
    </div>
  );
}

/**
 * Email/phone + password — kept for accounts made before Telegram Login.
 * `onSubmit` resolves to an error message, or null on success.
 */
export function PasswordSignInFields({
  onSubmit,
  submitLabel = 'Войти',
  autoFocus = true,
}: {
  onSubmit: (login: string, password: string) => Promise<string | null>;
  submitLabel?: string;
  autoFocus?: boolean;
}) {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      className="space-y-5"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        setError(await onSubmit(login.trim(), password));
        setBusy(false);
      }}
    >
      <Field label="Email или телефон">
        {(a) => (
          <Input
            {...a}
            size="lg"
            autoFocus={autoFocus}
            autoComplete="username"
            inputMode="email"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
          />
        )}
      </Field>
      <Field label="Пароль">
        {(a) => (
          <Input
            {...a}
            size="lg"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        )}
      </Field>
      {error && <Notice tone="danger">{error}</Notice>}
      <Button
        type="submit"
        variant="primary"
        size="lg"
        block
        loading={busy}
        disabled={!login || !password}
      >
        {submitLabel}
      </Button>
    </form>
  );
}

/** Password sign-in as a whole screen. */
export function LoginForm({
  title,
  subtitle,
  onSubmit,
  footer,
  brandSubtitle,
}: {
  title: string;
  subtitle?: string;
  onSubmit: (login: string, password: string) => Promise<string | null>;
  footer?: ReactNode;
  brandSubtitle?: string;
}) {
  return (
    <AuthScreen brandSubtitle={brandSubtitle} footer={footer}>
      <AuthHeading title={title} subtitle={subtitle} />
      <PasswordSignInFields onSubmit={onSubmit} />
    </AuthScreen>
  );
}
